import type { FormEvent, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { FormError } from '@/components/form/FormError';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface FormDialogProps {
  title: ReactNode;
  description?: ReactNode;
  submitLabel: string;
  submitIcon: LucideIcon;
  /** Bouton d'action rouge (suppression, retrait) */
  destructive?: boolean;
  /** Faux : bouton d'action désactivé et Entrée sans effet (champ requis vide…) */
  canSubmit?: boolean;
  submitting: boolean;
  error: string | null;
  onSubmit: () => void;
  onClose: () => void;
  /** Faux : masqué sans être démonté (le temps d'un dialogue ouvert par-dessus) */
  open?: boolean;
  /** Champs du formulaire */
  children?: ReactNode;
  /** Panneau qui monte du bas de l'écran, pleine largeur (saisie sur mobile), au lieu du dialogue centré */
  sheet?: boolean;
}

/** Panneau bas : collé au bas de l'écran (zone sûre des iPhone comprise), défilant s'il dépasse, qui glisse au lieu de zoomer */
const SHEET_CLASSES =
  'top-auto bottom-0 left-0 translate-x-0 translate-y-0 max-w-none sm:max-w-none rounded-b-none max-h-[90dvh] ' +
  'overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))] data-[state=open]:zoom-in-100 ' +
  'data-[state=closed]:zoom-out-100 data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom';

/**
 * Dialogue à formulaire : titre, champs, erreur d'envoi et pied Annuler / action, dans un vrai <form>. La validation
 * est celle de l'écran (noValidate), pas celle du navigateur.
 */
export function FormDialog({
  title,
  description,
  submitLabel,
  submitIcon: SubmitIcon,
  destructive = false,
  canSubmit = true,
  submitting,
  error,
  onSubmit,
  onClose,
  open = true,
  children,
  sheet = false,
}: FormDialogProps) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit && !submitting) onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className={sheet ? SHEET_CLASSES : undefined}>
        {/* Tout le dialogue est le formulaire : le bouton d'action le soumet, Entrée aussi depuis un champ texte */}
        <form onSubmit={handleSubmit} noValidate className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {children && <div className="space-y-4">{children}</div>}
          <FormError error={error} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size={sheet ? 'lg' : 'default'}
              className={sheet ? 'h-11' : undefined}
              onClick={onClose}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              size={sheet ? 'lg' : 'default'}
              className={sheet ? 'h-11' : undefined}
              variant={destructive ? 'destructive' : 'default'}
              disabled={!canSubmit || submitting}
            >
              <SubmitIcon className="w-4 h-4" />
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
